<template>
  <header class="global-header__pc">
    <div class="global-header__top">
      <SpaInternalLink href="/" class="brand">
        <img :src="logoImage" alt="サービスロゴイメージ" class="brand__image" />
        <img :src="logoLabel" alt="サービスロゴラベル" class="brand__label" />
      </SpaInternalLink>
      <div class="actions">
        <template v-if="props.isLoading">
          <!-- loading中は非表示（ロゴのみ） -->
        </template>
        <template v-else>
          <GuestHeaderAuthLinks v-if="!isLoggedIn" variant="pills" />
          <div v-else class="user">
            <span class="user__name" title="ユーザー名">{{ displayName }}</span>

            <div ref="settingsRef" class="user__menu-wrapper">
              <button
                class="user__action user__action--menu"
                type="button"
                :aria-expanded="isSettingsOpen"
                aria-haspopup="menu"
                @click="toggleSettings"
              >
                <img :src="settingIcon" alt="" class="user__icon" aria-hidden="true" />
                <span class="user__label">設定</span>
              </button>
              <div v-if="isSettingsOpen" class="user__menu" role="menu">
                <template v-for="item in settingsMenuItems" :key="item.label">
                  <SpaInternalLink
                    v-if="item.href"
                    :href="item.href"
                    class="user__menu-item"
                    role="menuitem"
                  >
                    {{ item.label }}
                  </SpaInternalLink>
                  <button
                    v-else-if="item.action === 'logout'"
                    class="user__menu-item"
                    type="button"
                    role="menuitem"
                    @click="onLogout"
                  >
                    {{ item.label }}
                  </button>
                  <button v-else class="user__menu-item" type="button" role="menuitem">
                    {{ item.label }}
                  </button>
                </template>
              </div>
            </div>

            <SpaInternalLink href="/faq" class="user__action user__action--link">
              <img :src="faqIcon" alt="" class="user__icon" aria-hidden="true" />
              <span class="user__label">FAQ</span>
            </SpaInternalLink>
          </div>
        </template>
      </div>
    </div>

    <PcGlobalNav
      v-if="isLoggedIn && !props.isLoading"
      :nav-items="navItems"
      :is-active="isActive"
      :platform="platform"
    />
  </header>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import faqIcon from '@/assets/images/icons/global-header-faq.svg';
import settingIcon from '@/assets/images/icons/global-header-setting.svg';
import logoImage from '@/assets/images/service-site-logo-image.svg';
import logoLabel from '@/assets/images/service-site-logo-label.svg';
import SpaInternalLink from '@/components/shared/SpaInternalLink.vue';
import GuestHeaderAuthLinks from './GuestHeaderAuthLinks.vue';
import PcGlobalNav from './PcGlobalNav.vue';
import type { GlobalHeaderProps, NavItem, SettingsMenuItem } from './types.d';

const props = defineProps<
  GlobalHeaderProps & {
    navItems: NavItem[];
    settingsMenuItems: SettingsMenuItem[];
    isActive: (item: NavItem) => boolean;
    onLogout: () => void;
    isLoading?: boolean;
    platform?: string;
  }
>();

const displayName = computed(() => {
  const trimmed = props.userName?.trim() ?? '';
  if (!trimmed) return '';
  return `${trimmed} 様`;
});

const isSettingsOpen = ref(false);
const settingsRef = ref<HTMLElement | null>(null);

const toggleSettings = () => {
  isSettingsOpen.value = !isSettingsOpen.value;
};

const closeSettings = () => {
  isSettingsOpen.value = false;
};

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Node | null;
  if (!settingsRef.value || !target) return;
  if (!settingsRef.value.contains(target)) {
    closeSettings();
  }
};

const handleEscape = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    closeSettings();
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('keydown', handleEscape);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('keydown', handleEscape);
});
</script>

<style scoped lang="scss">
@import '@/assets/css/_variables.scss';
@import '@/assets/stylesheets/components/_variables.scss';

.global-header__pc {
  width: min(1150px, calc(100% - 96px));
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: $z-navigation;
  margin: 0 auto;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 0 48px #1e1e1e17;
  border-radius: 12px;
  overflow: visible;
}

.global-header__top {
  width: 100%;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
  box-sizing: border-box;
  gap: 20px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.brand__image {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  object-fit: cover;
}

.brand__label {
  height: 22.9px;
  width: auto;
}

.actions {
  display: flex;
  align-items: flex-start;
}

.user {
  display: inline-flex;
  align-items: center;
  gap: 32px;
  color: #202020;
  height: 44px;
}

.user__name {
  font-weight: 400;
  font-size: 14px;
  letter-spacing: 0.62px;
  line-height: 15px;
  white-space: nowrap;
}

.user__action {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  padding: 0;
  height: 44px;
  cursor: pointer;
  color: #202020;
  text-decoration: none;
}

.user__icon {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.user__action--link {
  color: inherit;
}

.user__label {
  font-size: 10px;
  line-height: 1.4;
  letter-spacing: 0.62px;
  color: inherit;
}

.user__menu-wrapper {
  position: relative;
}

.user__menu {
  position: absolute;
  top: 52px;
  right: 0;
  min-width: 220px;
  padding: 8px 0;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 8px 24px #1e1e1e17;
  z-index: $z-global-header-dropdown;
}

.user__menu-item {
  width: 100%;
  padding: 10px 16px;
  text-align: left;
  border: none;
  background: transparent;
  color: #202020;
  font-size: 12px;
  line-height: 1.4;
  text-decoration: none;
  display: block;
  box-sizing: border-box;
  cursor: pointer;
}

.user__menu-item:hover,
.user__menu-item:focus-visible {
  background: #f7f8fb;
  outline: none;
}
</style>
